import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import EmployeeForm, { PendingEmployeeDocument } from '../../components/HR/EmployeeForm';
import EntityDocumentsModal from '../../components/HR/EntityDocumentsModal';
import { employeeDocumentsService, employeesService } from '../../services';
import { PageHeader, FilterBar, ErrorState, Pagination } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { Employee, TableColumn } from '../../types';
import { useBranches } from '../../hooks/useBranches';
import { useRanks } from '../../hooks/useRanks';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { getArabicRankName } from '../../utils/rankUtils';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import {
  useEmployees,
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from '../../hooks/useEmployees';

const Employees: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | undefined>();
  const [userRole] = useState('admin');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');
  const [nationalityFilter, setNationalityFilter] = useState<string>('all');
  const [travelPermitFilter, setTravelPermitFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [salaryFromFilter, setSalaryFromFilter] = useState<string>('');
  const [salaryToFilter, setSalaryToFilter] = useState<string>('');
  const [documentsEmployee, setDocumentsEmployee] = useState<Employee | undefined>();

  const debouncedSearch = useDebouncedValue(searchTerm);

  // All filters run server-side so search and pagination cover the whole dataset
  const serverFilters = useMemo(() => {
    const filters: any = { employee_status: { $eq: 'active' } };
    if (branchFilter !== 'all') filters.branch = { id: { $eq: branchFilter } };
    if (rankFilter !== 'all') filters.rank = { $eq: rankFilter };
    if (nationalityFilter !== 'all') filters.nationality = { $eq: nationalityFilter };
    if (travelPermitFilter !== 'all') filters.travelPermit = { $eq: travelPermitFilter === 'yes' };
    if (dateFromFilter || dateToFilter) {
      filters.hireDate = {
        ...(dateFromFilter ? { $gte: dateFromFilter } : {}),
        ...(dateToFilter ? { $lte: dateToFilter } : {}),
      };
    }
    if (userRole === 'admin' && (salaryFromFilter || salaryToFilter)) {
      filters.salary = {
        ...(salaryFromFilter ? { $gte: salaryFromFilter } : {}),
        ...(salaryToFilter ? { $lte: salaryToFilter } : {}),
      };
    }
    return filters;
  }, [branchFilter, rankFilter, nationalityFilter, travelPermitFilter,
    dateFromFilter, dateToFilter, salaryFromFilter, salaryToFilter, userRole]);

  const filtersKey = JSON.stringify(serverFilters) + debouncedSearch;
  useEffect(() => { setPage(1); }, [filtersKey]);

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'code', order: 'asc' as const }],
    pagination: { page, pageSize },
    filters: serverFilters,
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
  }), [page, pageSize, serverFilters, debouncedSearch]);

  const lookupOptions = useMemo(() => ({
    sort: [{ field: 'name', order: 'asc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: employees, meta, isLoading, error, refetch } = useEmployees(queryOptions);
  const { data: branches = [] } = useBranches(lookupOptions);
  const { data: ranks = [] } = useRanks(lookupOptions);
  const totalCount = meta?.pagination?.total ?? employees?.length ?? 0;

  const createMutation = useCreateEmployee(() => {
    setShowForm(false);
    setEditingEmployee(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateEmployee(() => {
    setShowForm(false);
    setEditingEmployee(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteEmployee(() => {
    setTimeout(() => refetch(), 100);
  });

  const fetchEmployeeDocuments = useCallback(async () => {
    if (!documentsEmployee) return [];
    const response: any = await employeeDocumentsService.getAll({
      filters: { employee: { documentId: { $eq: documentsEmployee.documentId } } },
    });
    return response.data || [];
  }, [documentsEmployee]);

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const rankOptions = useMemo(() =>
    Array.from(new Set(ranks.map((r: any) => r.name).filter(Boolean))).sort(),
  [ranks]);

  const uniqueNationalities = useMemo(() => {
    if (!employees) return [];
    return Array.from(new Set(employees.map(emp => emp.nationality).filter(Boolean)));
  }, [employees]);

  // Exports cover the entire filtered dataset, not just the visible page
  const fetchAllFiltered = useCallback(async (): Promise<Employee[]> => {
    const response: any = await employeesService.getAll({
      sort: [{ field: 'code', order: 'asc' as const }],
      filters: serverFilters,
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
    });
    return response.data || [];
  }, [serverFilters, debouncedSearch]);

  const handleExport = async () => {
    const all = await fetchAllFiltered();
    const exportData = all.map(emp => ({
      Code: emp.code, Name: emp.name, 'Arabic Name': emp.arabicName, Rank: emp.rank,
      'الوظيفة': getArabicRankName(ranks, emp.rank, emp.position),
      'National ID': emp.nationalId, 'Card Expiry': emp.cardExpiryDate, Address: emp.address, 'Birth Date': emp.birthDate,
      'Seaman Book Number': emp.seamanBookNumber, 'Seaman Book Expiry': emp.seamanBookExpiry, Nationality: emp.nationality,
      'Travel Permit': emp.travelPermit ? 'Yes' : 'No', 'Phone Number': emp.phoneNumber,
      ...(userRole === 'admin' && { Salary: emp.salary }), 'Amount (Allowance)': emp.amount,
      'Vessel/Headquarters': getBranchName(emp.branchId), 'Hire Date': emp.hireDate
    }));
    exportToExcel(exportData, `employees_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = async () => {
    const all = await fetchAllFiltered();
    const exportData = all.map(emp => ({
      Code: emp.code, Name: emp.name, 'Arabic Name': emp.arabicName, Rank: emp.rank,
      'الوظيفة': getArabicRankName(ranks, emp.rank, emp.position),
      'National ID': emp.nationalId, 'Card Expiry': emp.cardExpiryDate, Address: emp.address, 'Birth Date': emp.birthDate,
      'Seaman Book Number': emp.seamanBookNumber, 'Seaman Book Expiry': emp.seamanBookExpiry, Nationality: emp.nationality,
      'Travel Permit': emp.travelPermit ? 'Yes' : 'No', 'Phone Number': emp.phoneNumber, Amount: emp.amount
    }));
    printAsExcel(exportData, 'Employees');
  };

  const columns: TableColumn[] = [
    { key: 'code', label: 'Code', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'arabicName', label: 'الاسم', sortable: true },
    { key: 'rank', label: 'Rank', sortable: true },
    { key: 'position', label: 'الوظيفة', sortable: false, render: (_value: string, item: Employee) => getArabicRankName(ranks, item.rank, item.position) },
    { key: 'nationalId', label: 'الرقم القومي', sortable: true },
    { key: 'cardExpiryDate', label: 'تاريخ انتهاء البطاقة', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'address', label: 'العنوان', sortable: true },
    { key: 'birthDate', label: 'Date of birth', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'seamanBookNumber', label: "Seaman's book number", sortable: true },
    { key: 'seamanBookExpiry', label: "Expiry date of seaman's book", sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'nationality', label: 'Nationality', sortable: true },
    { key: 'travelPermit', label: 'Travel permit', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    ) },
    { key: 'phoneNumber', label: 'Mobile number', sortable: false },
    ...(userRole !== 'hr' ? [{ key: 'salary', label: 'Salary', sortable: true, render: (value: number | null) => `${(value ?? 0).toLocaleString()} EGP` }] : []),
    { key: 'amount', label: 'Amount (Allowance)', sortable: true, render: (value: number | null) => `${(value ?? 0).toLocaleString()} EGP` },
    { key: 'documents', label: 'المستندات', sortable: false, render: (_: unknown, item: Employee) => (
      <button
        type="button"
        className="text-ocean-600 hover:text-ocean-800 underline"
        onClick={() => setDocumentsEmployee(item)}
      >
        عرض
      </button>
    ) }
  ];

  const handleEdit = (employee: Employee) => { setEditingEmployee(employee); setShowForm(true); };
  const handleDelete = async (employee: Employee) => {
    if (!window.confirm(`Are you sure you want to delete ${employee.name}?`)) return;
    if (!employee?.id) return;
    try {
      await deleteMutation.execute(employee);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };
  const handleAdd = () => { setEditingEmployee(undefined); setShowForm(true); };

  const handleFormSubmit = async (data: Partial<Employee>, pendingDocument?: PendingEmployeeDocument) => {
    try {
      let savedEmployeeDocumentId: string | undefined = editingEmployee?.documentId;
      if (editingEmployee?.id) {
        await updateMutation.execute({ id: editingEmployee.id, data: { ...data, documentId: editingEmployee.documentId } });
      } else {
        const created: any = await createMutation.execute(data);
        await bumpCodePrefixSequence(data.code);
        savedEmployeeDocumentId = created?.documentId || created?.data?.documentId;
      }
      if (pendingDocument && savedEmployeeDocumentId) {
        await employeeDocumentsService.create({
          employeeId: savedEmployeeDocumentId,
          documentType: pendingDocument.documentType,
          documentNumber: pendingDocument.documentNumber,
          issueDate: pendingDocument.issueDate || null,
          expiryDate: pendingDocument.expiryDate || null,
          status: 'valid',
          filePath: pendingDocument.filePath,
        });
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => { setShowForm(false); setEditingEmployee(undefined); };

  const handleClearFilters = () => {
    setSearchTerm('');
    setBranchFilter('all');
    setRankFilter('all');
    setNationalityFilter('all');
    setTravelPermitFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setSalaryFromFilter('');
    setSalaryToFilter('');
  };

  const baseFilters: FilterConfig[] = [
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [
      { value: 'all', label: 'All Vessels/HQ' }, ...branches.map((b: any) => ({ value: b.id, label: b.name }))
    ]},
    { key: 'rank', label: 'Rank', type: 'select', value: rankFilter, onChange: setRankFilter, options: [
      { value: 'all', label: 'All Ranks' }, ...rankOptions.map(r => ({ value: r, label: r }))
    ]},
    { key: 'nationality', label: 'Nationality', type: 'select', value: nationalityFilter, onChange: setNationalityFilter, options: [
      { value: 'all', label: 'All Nationalities' }, ...uniqueNationalities.map(n => ({ value: n, label: n }))
    ]},
    { key: 'travelPermit', label: 'Travel Permit', type: 'select', value: travelPermitFilter, onChange: setTravelPermitFilter, options: [
      { value: 'all', label: 'All' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }
    ]},
    { key: 'dateFrom', label: 'Hire Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Hire Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const filterConfigs: FilterConfig[] = userRole === 'admin'
    ? [...baseFilters,
      { key: 'salaryFrom', label: 'Min Salary', type: 'number', value: salaryFromFilter, onChange: setSalaryFromFilter, placeholder: '0', step: '100' },
      { key: 'salaryTo', label: 'Max Salary', type: 'number', value: salaryToFilter, onChange: setSalaryToFilter, placeholder: '999999', step: '100' },
    ]
    : baseFilters;

  if (isLoading && !meta) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading employees. Please try again." onRetry={() => refetch()} entityName="employees" />;
  if (showForm) {
    return (
      <div className="space-y-6">
        <EmployeeForm employee={editingEmployee} onSubmit={handleFormSubmit} onCancel={handleFormCancel} userRole={userRole} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Employee Management" description="Manage your employees, their branches, and HR documents" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search employees..."
        filters={filterConfigs}
        onClearFilters={handleClearFilters}
        totalCount={totalCount}
        filteredCount={totalCount}
        entityName="Employees"
        onAdd={handleAdd}
        addLabel="Add Employee"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <Table columns={columns} data={employees || []} onEdit={handleEdit} onDelete={handleDelete} title="Employee Directory" showAddButton={false} searchable={false} />
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          isLoading={isLoading}
        />
      </div>
      {documentsEmployee && (
        <EntityDocumentsModal
          title={`مستندات ${documentsEmployee.arabicName || documentsEmployee.name}`}
          fetchDocuments={fetchEmployeeDocuments}
          onClose={() => setDocumentsEmployee(undefined)}
        />
      )}
    </div>
  );
};

export default Employees;
